import { useState, type JSX } from 'react';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Input, PriceInput, Select } from '@artosera/ui';
import { TYPES_MOUVEMENT, type TypeMouvement } from '@artosera/shared';
import { useValeursAssurance, messageErreurAssurance } from '../../api/assurance.js';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { useCreerMouvement } from '../../api/mouvements.js';
import { useOeuvre, type OeuvrePresentee } from '../../api/oeuvres.js';
import { type ContactPresente } from '../../api/contacts.js';
import { useLieux } from '../../api/lieux.js';
import { estApiError } from '../../api/errors.js';
import { RechercheContact } from '../ventes/RechercheContact.js';
import { RechercheOeuvre } from '../ventes/RechercheOeuvre.js';
import { Field } from '../Field.js';
import styles from './MouvementDetailPage.module.css';

export function NouveauMouvementDialog({
  galerieSlug,
  registrePoliceActif = false,
  ouvert,
  onOuvertChange,
  oeuvreInitialeId,
}: {
  galerieSlug: string;
  registrePoliceActif?: boolean;
  ouvert: boolean;
  onOuvertChange: (v: boolean) => void;
  oeuvreInitialeId?: string | undefined;
}): JSX.Element {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const naviguer = useNavigate();
  const creer = useCreerMouvement(galerieSlug);
  const initiale = useOeuvre(galerieSlug, oeuvreInitialeId ?? null);
  const lieux = useLieux(galerieSlug);
  const [depotRecu, setDepotRecu] = useState(false);
  const [type, setType] = useState<TypeMouvement>('sortie');
  const [ajoutees, setAjoutees] = useState<OeuvrePresentee[]>([]);
  const [initialeRetiree, setInitialeRetiree] = useState(false);
  const choisies = [
    ...(!initialeRetiree && initiale.data ? [initiale.data.oeuvre] : []),
    ...ajoutees,
  ];
  const [contact, setContact] = useState<ContactPresente | null>(null);
  const [transporteur, setTransporteur] = useState<ContactPresente | null>(null);
  const [de, setDe] = useState('');
  const [vers, setVers] = useState('');
  const [depart, setDepart] = useState(new Date().toISOString().slice(0, 10));
  const [retour, setRetour] = useState('');
  const [valeur, setValeur] = useState<number | null>(null);
  const [assureur, setAssureur] = useState<string | null>(null);
  const [police, setPolice] = useState<string | null>(null);
  const contexte = useShellContext();
  const receptionDepots =
    registrePoliceActif ||
    Boolean(contexte?.galerie && galerieAModule(contexte.galerie, 'espace-deposant'));
  const assuranceActive = Boolean(
    contexte?.galerie &&
    galerieAModule(contexte.galerie, 'valeurs-assurance') &&
    galerieALaction(contexte.galerie, 'insurance:read'),
  );
  const valeurs = useValeursAssurance(
    galerieSlug,
    choisies.map((o) => o.id),
    ouvert && assuranceActive,
  );
  const proposition = assuranceActive ? valeurs.data : undefined;
  const devises = new Set(
    choisies.map((o) => proposition?.lignes.find((l) => l.oeuvreId === o.id)?.devise ?? o.devise),
  );
  const devisesMixtes = assuranceActive && devises.size > 1;
  const deviseProposee =
    proposition?.lignes.find((l) => l.oeuvreId === choisies[0]?.id)?.devise ??
    choisies[0]?.devise ??
    'EUR';
  const valeurProposee = choisies.reduce(
    (s, o) =>
      s +
      (proposition?.lignes.find((l) => l.oeuvreId === o.id)?.valeurCentimes ??
        o.prixPublicCentimes ??
        0),
    0,
  );
  const assureurPropose = assureur ?? proposition?.policeProposee?.assureur ?? '';
  const policeProposee = police ?? proposition?.policeProposee?.numero ?? '';
  const [numero, setNumero] = useState('');
  const [url, setUrl] = useState('');
  const [erreur, setErreur] = useState('');
  const [retourErreur, setRetourErreur] = useState(false);
  const [conflit, setConflit] = useState<string | null>(null);
  const label = (cle: string) => t(`regie:mouvements.creation.${cle}`);
  const optionsLieux = [
    { value: '', label: '—' },
    ...(lieux.data ?? []).map((l) => ({ value: l.id, label: l.nom })),
  ];
  async function soumettre() {
    setErreur('');
    setConflit(null);
    setRetourErreur(false);
    if (
      assuranceActive &&
      (valeurs.isFetching || valeurs.isError || (devisesMixtes && valeur === null))
    )
      return;
    if (type === 'pret' && !retour) {
      setRetourErreur(true);
      return;
    }
    if (!choisies.length || !depart) {
      setErreur(label('oeuvresRequises'));
      return;
    }
    try {
      const reponse = await creer.mutateAsync({
        type,
        ...(receptionDepots ? { depotRecu } : {}),
        oeuvres: choisies.map((o) => o.id),
        departLe: depart,
        deId: de || null,
        versId: vers || null,
        contactId: contact?.id ?? null,
        transporteurId: transporteur?.id ?? null,
        retourPrevuLe: retour || null,
        ...(valeur !== null || assureurPropose || policeProposee
          ? {
              assurance: {
                valeurCentimes: valeur ?? valeurProposee,
                devise: assuranceActive ? deviseProposee : 'EUR',
                ...(assureurPropose ? { assureur: assureurPropose } : {}),
                ...(policeProposee ? { police: policeProposee } : {}),
              },
            }
          : {}),
        ...(numero || url
          ? {
              suivi: {
                ...(numero ? { numeroSuivi: numero } : {}),
                ...(url ? { urlSuivi: url } : {}),
              },
            }
          : {}),
      });
      onOuvertChange(false);
      void naviguer(`/${galerieSlug}/mouvements/${reponse.mouvement.id}`);
    } catch (e) {
      setErreur(estApiError(e) ? e.message : t('commun:erreurReseau'));
      if (
        estApiError(e) &&
        typeof e.details === 'object' &&
        e.details !== null &&
        'raison' in e.details &&
        e.details.raison === 'oeuvre_deja_en_mouvement' &&
        'mouvementId' in e.details &&
        typeof e.details.mouvementId === 'string'
      )
        setConflit(e.details.mouvementId);
    }
  }
  return (
    <Dialog
      open={ouvert}
      onOpenChange={onOuvertChange}
      title={label('titre')}
      closeLabel={t('commun:actions.fermer')}
    >
      <form
        className={styles.form}
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void soumettre();
        }}
      >
        <Field htmlFor="m-type" label={label('type')}>
          <Select
            id="m-type"
            aria-label={label('type')}
            value={type}
            onValueChange={(v) => setType(v as TypeMouvement)}
            options={TYPES_MOUVEMENT.map((v) => ({
              value: v,
              label: t(`regie:mouvements.types.${v}`),
            }))}
          />
        </Field>
        {receptionDepots && (
          <label>
            <input
              type="checkbox"
              checked={depotRecu}
              onChange={(event) => setDepotRecu(event.target.checked)}
            />{' '}
            {t('regie:registrePolice.depotRecu')}
          </label>
        )}
        <fieldset>
          <legend>{label('oeuvres')}</legend>
          {choisies.map((o) => (
            <div key={o.id} className={styles.oeuvre}>
              <span>
                {o.artisteNom} — {o.titre}
              </span>
              <Button
                type="button"
                onClick={() => {
                  if (o.id === oeuvreInitialeId) setInitialeRetiree(true);
                  setAjoutees(ajoutees.filter((a) => a.id !== o.id));
                }}
              >
                {label('retirer')}
              </Button>
            </div>
          ))}
          <RechercheOeuvre
            galerieSlug={galerieSlug}
            onChoisir={(o) => {
              if (!choisies.some((a) => a.id === o.id)) setAjoutees([...ajoutees, o]);
            }}
          />
        </fieldset>
        <fieldset>
          <legend>{t(`regie:mouvements.parties.${type}`)}</legend>
          <RechercheContact
            galerieSlug={galerieSlug}
            contact={contact}
            onChoisir={setContact}
            onEffacer={() => setContact(null)}
          />
        </fieldset>
        <div className={styles.grid}>
          <Field htmlFor="m-de" label={label('dePuis')}>
            <Select id="m-de" value={de} onValueChange={setDe} options={optionsLieux} />
          </Field>
          <Field htmlFor="m-vers" label={label('versLieu')}>
            <Select id="m-vers" value={vers} onValueChange={setVers} options={optionsLieux} />
          </Field>
          <Field htmlFor="m-depart" label={label('departLe')}>
            <Input
              id="m-depart"
              type="date"
              required
              value={depart}
              onChange={(e) => setDepart(e.target.value)}
            />
          </Field>
          <Field
            htmlFor="m-retour"
            label={label('retourPrevuLe')}
            error={retourErreur ? label('retourRequis') : undefined}
          >
            <Input
              id="m-retour"
              type="date"
              min={depart}
              required={type === 'pret'}
              value={retour}
              onChange={(e) => setRetour(e.target.value)}
            />
          </Field>
        </div>
        <details key={type === 'pret' ? 'pret' : 'autre'} open={type === 'pret' ? true : undefined}>
          <summary>{label('assurance')}</summary>
          <div className={styles.form}>
            <Field
              htmlFor="m-valeur"
              label={t(
                assuranceActive
                  ? 'regie:mouvements.creation.valeurAssureeProposee'
                  : 'regie:mouvements.fiche.valeurAssuree',
              )}
            >
              <PriceInput
                id="m-valeur"
                lang={i18n.language === 'en' ? 'en' : 'fr'}
                currency={assuranceActive ? deviseProposee : 'EUR'}
                cents={valeur ?? (devisesMixtes ? 0 : valeurProposee)}
                onCentsChange={setValeur}
              />
            </Field>
            <Field htmlFor="m-assureur" label={t('regie:mouvements.fiche.assureur')}>
              <Input
                id="m-assureur"
                value={assureurPropose}
                onChange={(e) => setAssureur(e.target.value)}
              />
            </Field>
            <Field htmlFor="m-police" label={t('regie:mouvements.fiche.police')}>
              <Input
                id="m-police"
                value={policeProposee}
                onChange={(e) => setPolice(e.target.value)}
              />
            </Field>
          </div>
        </details>
        <details>
          <summary>{label('transport')}</summary>
          <div className={styles.form}>
            <RechercheContact
              galerieSlug={galerieSlug}
              contact={transporteur}
              onChoisir={setTransporteur}
              onEffacer={() => setTransporteur(null)}
            />
            <Field htmlFor="m-suivi" label={t('regie:mouvements.fiche.numeroSuivi')}>
              <Input id="m-suivi" value={numero} onChange={(e) => setNumero(e.target.value)} />
            </Field>
            <Field htmlFor="m-url" label={t('regie:mouvements.fiche.urlSuivi')}>
              <Input id="m-url" type="url" value={url} onChange={(e) => setUrl(e.target.value)} />
            </Field>
          </div>
        </details>
        {assuranceActive && valeurs.isError && (
          <p role="alert">{messageErreurAssurance(valeurs.error)}</p>
        )}
        {devisesMixtes && <p role="alert">{t('regie:assurance.deviseDifferente')}</p>}
        {erreur && <p role="alert">{erreur}</p>}
        {conflit && (
          <Link to={`/${galerieSlug}/mouvements/${conflit}`}>{label('voirMouvement')}</Link>
        )}
        <Button
          type="submit"
          loading={creer.isPending}
          disabled={
            assuranceActive &&
            (valeurs.isFetching || valeurs.isError || (devisesMixtes && valeur === null))
          }
        >
          {label('creer')}
        </Button>
      </form>
    </Dialog>
  );
}
